import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Eye, Share2, Bookmark, ThumbsUp, Youtube } from "lucide-react";
import { toast } from "sonner";
import { fetchArticle, fetchArticles, catLabel, catAccent, timeAgo, readTime, likeArticle, isBookmarked, toggleBookmark } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { WhatsAppShareButton, XShareButton, shareLink } from "@/components/WhatsAppShare";

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setArticle(null);
    setLiked(false);
    setSaved(isBookmarked(id));
    fetchArticle(id)
      .then((a) => {
        setArticle(a);
        setLikes(a.likes || 0);
        fetchArticles({ category: a.category, limit: 4 }).then((r) =>
          setRelated(r.filter((x) => x.id !== a.id).slice(0, 3))
        );
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const share = () => {
    navigator.clipboard.writeText(shareLink(id));
    toast.success("लिंक कॉपी हो गया!");
  };

  const like = async () => {
    if (liked) return;
    setLiked(true);
    setLikes((l) => l + 1);
    try {
      const res = await likeArticle(id);
      setLikes(res.likes);
    } catch {}
  };

  const save = () => {
    const now = toggleBookmark(id);
    setSaved(now);
    toast.success(now ? "लेख सहेजा गया!" : "बुकमार्क हटाया गया");
  };

  if (notFound)
    return (
      <div className="pt-48 pb-40 text-center">
        <p className="font-display text-3xl text-white">लेख नहीं मिला</p>
        <Link to="/" className="text-[#FFD400] font-mono-meta text-sm mt-4 inline-block">← होम पर लौटें</Link>
      </div>
    );
  if (!article) return <div className="pt-48 pb-40" />;

  return (
    <main className="pt-32 pb-16" data-testid="article-detail-view">
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Link to="/" className="inline-flex items-center gap-2 font-mono-meta text-xs text-slate-400 hover:text-[#FFD400] uppercase tracking-widest transition-colors mb-8">
            <ArrowLeft size={13} /> वापस
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <span
              className="font-mono-meta text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1"
              style={{ color: catAccent(article.category), border: `1px solid ${catAccent(article.category)}50` }}
            >
              {catLabel(article.category)}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.25] text-white">
            {article.title}
          </h1>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed font-editorial">{article.summary}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 border-y border-white/10 py-4 font-mono-meta text-xs text-slate-400">
            <span className="text-[#FFD400] font-semibold">{article.author}</span>
            <span className="flex items-center gap-1.5"><Clock size={12} /> {timeAgo(article.created_at)}</span>
            <span>{readTime(article.content)} पढ़ने का समय</span>
            <span className="flex items-center gap-1.5"><Eye size={12} /> {article.views.toLocaleString("hi-IN")}</span>
            <button
              onClick={share}
              data-testid="share-article-button"
              className="ml-auto flex items-center gap-1.5 text-white hover:text-[#FFD400] transition-colors"
            >
              <Share2 size={13} /> शेयर
            </button>
            <WhatsAppShareButton article={article} />
            <XShareButton article={article} />
            <button
              onClick={save}
              data-testid="bookmark-article-button"
              className={`flex items-center gap-1.5 transition-colors ${saved ? "text-[#FFD400]" : "text-white hover:text-[#FFD400]"}`}
            >
              <Bookmark size={13} fill={saved ? "currentColor" : "none"} /> {saved ? "सहेजा गया" : "सहेजें"}
            </button>
            <button
              onClick={like}
              data-testid="like-article-button"
              className={`flex items-center gap-1.5 transition-colors ${liked ? "text-[#FFD400]" : "text-white hover:text-[#FFD400]"}`}
            >
              <ThumbsUp size={13} fill={liked ? "currentColor" : "none"} /> {likes.toLocaleString("hi-IN")}
            </button>
          </div>
        </motion.div>

        {article.youtube_video_id ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8"
            data-testid="article-video-embed"
          >
            <div className="border border-white/10 bg-black aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${article.youtube_video_id}`}
                title={article.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            {article.youtube_url && (
              <a
                href={article.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="watch-on-youtube-link"
                className="mt-3 inline-flex items-center gap-2 font-mono-meta text-xs text-slate-400 hover:text-[#FF375F] uppercase tracking-widest transition-colors"
              >
                <Youtube size={14} /> YouTube पर देखें
              </a>
            )}
          </motion.div>
        ) : (
          article.image_url && (
            <motion.div
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-8 border border-white/10 overflow-hidden"
            >
              <img src={article.image_url} alt={article.title} className="w-full aspect-[16/9] object-cover" />
            </motion.div>
          )
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-10 space-y-6"
        >
          {article.content.split("\n\n").map((p, i) => (
            <p key={i} className="text-lg leading-[1.9] text-slate-200">
              {p}
            </p>
          ))}
        </motion.div>
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-8">संबंधित खबरें</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
